import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { ArrowLeft, User, Crown } from 'lucide-react';
import { toast } from 'sonner';

const Settings = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [canceling, setCanceling] = useState(false);

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      await api.post('/payments/cancel-subscription');
      toast.success('Subscription canceled successfully');
      await refreshUser(); // Refresh user data to show updated subscription status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-green-50" data-testid="settings-page">
      <header className="bg-white shadow-sm border-b border-red-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold mb-8">
          <span className="gradient-text">⚙️ Settings</span>
        </h1>

        <div className="space-y-6">
          {/* Account Info */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <User className="w-12 h-12 text-red-600" />
                <div>
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-sm text-gray-600">{user?.email}</p>
                </div>
              </div>
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Subscription Status</p>
                    <p className="text-sm text-gray-600">
                      {user?.subscription_tier === 'elite' ? (
                        <span className="flex items-center gap-1 text-amber-600">
                          <Crown className="w-4 h-4" />
                          Elite Active
                        </span>
                      ) : user?.subscription_tier === 'premium' ? (
                        <span className="flex items-center gap-1 text-red-600">
                          <Crown className="w-4 h-4" />
                          Premium Active
                        </span>
                      ) : (
                        'Free Plan'
                      )}
                    </p>
                    {user?.subscription_end_date && (
                      <p className="text-xs text-gray-500 mt-1">
                        Renews on {new Date(user.subscription_end_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {user?.subscription_tier === 'free' && (
                      <Button
                        onClick={() => navigate('/subscription')}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="upgrade-button"
                      >
                        Upgrade
                      </Button>
                    )}
                    {(user?.subscription_tier === 'premium' || user?.subscription_tier === 'elite') && (
                      <>
                        <Button
                          onClick={() => navigate('/subscription')}
                          variant="outline"
                          data-testid="manage-subscription-button"
                        >
                          Manage Plan
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid="cancel-subscription-button"
                            >
                              Cancel Subscription
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to cancel your {user?.subscription_tier} subscription?
                                You'll lose access to premium features like recipient profiles, gift history,
                                and export options.
                                {user?.subscription_end_date && (
                                  <span className="block mt-2 font-medium">
                                    You'll retain access until {new Date(user.subscription_end_date).toLocaleDateString()}.
                                  </span>
                                )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={handleCancelSubscription}
                                disabled={canceling}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                {canceling ? 'Canceling...' : 'Yes, Cancel'}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preferences */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Preferences</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">Currency: USD</p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Settings;
